/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import axios from 'axios';
import urlJoin from 'proper-url-join';

import {
  endpoints,
  isWxoEnabled
} from '@ibm-watson/shared/modules/ia/util/config';
import { GeneralQueryParam, SpeechQueryParam } from '../../../../ts';
import { SpeechInstance } from '../../../../ts/speech';

const strings = {
  ERROR_INSTANCEID_NULL: 'Unable to get instance, instanceID is Null',
  ERROR_APIKEY_NULL: 'Unable to get Elevenlabs models, apiKey is Null'
};

const WATSON = {
  stt_instances: '/speech_to_text/instances',
  stt_instance: '/speech_to_text/instance',
  tts_instances: '/text_to_speech/instances',
  tts_instance: '/text_to_speech/instance'
};

const enum CPD {
  stt_models = '/cpd/stt',
  stt_url = '/cpd/stt/modelUrl',
  tts_voices = '/cpd/tts',
  tts_url = '/cpd/tts/voiceUrl',
  tts_synthesize = '/cpd/tts/synthesize'
}

export const ELEVENLABS_API_BASE_ENDPOINT = 'https://api.elevenlabs.io';
export const ELEVENLABS_XI_API_KEY_HEADER = 'xi-api-key';

export const getSpeechToTextInstances = (
  allAccounts = true
): Promise<{ instances: SpeechInstance[] }> => {
  return axios
    .get(urlJoin(endpoints.bluemix, WATSON.stt_instances), {
      params: {
        allAccounts: allAccounts
      }
    })
    .then(response => response.data);
};

const invokeSpeechApi = async (instanceId: string, apiPath: CPD) => {
  const url = urlJoin(endpoints.bluemix, apiPath);

  const { data } = await axios.get(url, {
    params: {
      instanceId
    }
  });

  return data;
};

// this api is used only in CP4D. It takes in an instanceId
// which will be used to retrieve SST instance URL.
export const getSpeechToTextInstanceUrl = async (instanceId: string) =>
  invokeSpeechApi(instanceId, CPD.stt_url);

// this api is used only in CP4D. It takes in an instanceId
// which will be used to retrieve TTS instance URL.
export const getTextToSpeechInstanceUrl = async (instanceId: string) =>
  invokeSpeechApi(instanceId, CPD.tts_url);

// this api is used only in CP4D. It takes in an instanceId
// which will be used to retrieve SST models.
export const getSpeechToTextInstanceModels = async (instanceId: string) =>
  invokeSpeechApi(instanceId, CPD.stt_models);

// this api is used only in CP4D. It takes in an instanceId
// which will be used to retrieve TTS voices.
export const getTextToSpeechInstanceVoices = async (instanceId: string) =>
  invokeSpeechApi(instanceId, CPD.tts_voices);

// this api is used only in CP4D. It takes in an instanceId
// and fields relating to voice. This is used
// to preview voice.
export const synthesizeTextToSpeechInstanceInCpd = async params => {
  const url = urlJoin(endpoints.bluemix, CPD.tts_synthesize, {
    query: {
      [SpeechQueryParam.INSTANCE_ID]: params.instanceId,
      [SpeechQueryParam.TEXT]: params.text,
      [SpeechQueryParam.PITCH_PERCENTAGE]: params.pitch_percentage,
      [SpeechQueryParam.RATE_PERCENTAGE]: params.rate_percentage,
      [SpeechQueryParam.VOICE]: params.voice
    }
  });

  const { data } = await axios.get(url, { responseType: 'blob' });

  return data;
};

export const getSpeechToTextInstance = (
  instanceId: string
): Promise<SpeechInstance> => {
  if (!instanceId) {
    return Promise.reject(new Error(strings.ERROR_INSTANCEID_NULL));
  }

  // In the WXO environment we can not send the instance CRN as a path
  // parameter since a proxy in that environment will spuriously decode it
  // and break the routing on the server. We will thus send it as a query
  // parameter, which will retain its encoding.
  const url = urlJoin(
    endpoints.bluemix,
    WATSON.stt_instance,
    isWxoEnabled ? 'STT_INSTANCE_CRN' : encodeURIComponent(instanceId),
    {
      query: {
        [GeneralQueryParam.SPEECH_INSTANCE_CRN]: instanceId
      }
    }
  );

  return axios.get(url).then(response => {
    return response.data;
  });
};

export const createSpeechToTextInstance = (prefix: string, planId?: string) => {
  return axios
    .post(urlJoin(endpoints.bluemix, WATSON.stt_instances), {
      prefix: prefix || null,
      planId: planId || null
    })
    .then(response => {
      if (response && response.data && response.data.new_resource) {
        return response.data.new_resource;
      }
      return response.data;
    });
};

const createResourceCredentials = (path: string, instanceId: string) => {
  if (!instanceId) {
    return Promise.reject(new Error(strings.ERROR_INSTANCEID_NULL));
  }

  const url = urlJoin(
    endpoints.bluemix,
    path,
    encodeURIComponent(instanceId),
    'credentials'
  );

  return axios.post(url).then(response => {
    return response.data;
  });
};

export const createSpeechToTextCredentials = (instanceId: string) => {
  return createResourceCredentials(WATSON.stt_instance, instanceId);
};

export const createTextToSpeechCredentials = (instanceId: string) => {
  return createResourceCredentials(WATSON.tts_instance, instanceId);
};

export const getTextToSpeechInstances = (
  allAccounts = true
): Promise<{ instances: SpeechInstance[] }> => {
  return axios
    .get(urlJoin(endpoints.bluemix, WATSON.tts_instances), {
      params: {
        allAccounts: allAccounts
      }
    })
    .then(response => response.data);
};

export const getTextToSpeechInstance = (instanceId: string) => {
  if (!instanceId) {
    return Promise.reject(new Error(strings.ERROR_INSTANCEID_NULL));
  }

  // In the WXO environment we can not send the instance CRN as a path
  // parameter since a proxy in that environment will spuriously decode it
  // and break the routing on the server. We will thus send it as a query
  // parameter, which will retain its encoding.
  const url = urlJoin(
    endpoints.bluemix,
    WATSON.tts_instance,
    isWxoEnabled ? 'TTS_INSTANCE_CRN' : encodeURIComponent(instanceId),
    {
      query: {
        [GeneralQueryParam.SPEECH_INSTANCE_CRN]: instanceId
      }
    }
  );
  return axios.get(url).then(response => {
    return response.data;
  });
};

export const synthesizeTextToSpeechInstance = (
  instanceId: string,
  pitch_percentage: string,
  rate_percentage: string,
  text: string,
  voice: string
) => {
  if (!instanceId) {
    return Promise.reject(new Error(strings.ERROR_INSTANCEID_NULL));
  }

  // In the WXO environment we can not send the instance CRN as a path
  // parameter since a proxy in that environment will spuriously decode it
  // and break the routing on the server. We will thus send it as a query
  // parameter, which will retain its encoding.
  const url = urlJoin(
    endpoints.bluemix,
    WATSON.tts_instance,
    isWxoEnabled ? 'TTS_INSTANCE_CRN' : encodeURIComponent(instanceId),
    `/synthesize`,
    {
      query: {
        text,
        voice,
        rate_percentage,
        pitch_percentage,
        [GeneralQueryParam.SPEECH_INSTANCE_CRN]: instanceId
      }
    }
  );

  return axios.get(url, { responseType: 'blob' }).then(response => {
    return response.data;
  });
};

export const createTextToSpeechInstance = (prefix: string, planId?: string) => {
  return axios
    .post(urlJoin(endpoints.bluemix, WATSON.tts_instances), {
      prefix: prefix || null,
      planId: planId || null
    })
    .then(response => {
      if (response && response.data && response.data.new_resource) {
        return response.data.new_resource;
      }
      return response.data;
    });
};

export const fetchElevenLabsVoiceModels = async (apiKey: string) => {
  if (!apiKey) {
    return Promise.reject(new Error(strings.ERROR_APIKEY_NULL));
  }

  return axios
    .get(urlJoin(ELEVENLABS_API_BASE_ENDPOINT, 'v1', 'models'), {
      headers: { [ELEVENLABS_XI_API_KEY_HEADER]: apiKey }
    })
    .then(response => {
      return response.data;
    });
};

export const fetchElevenLabsVoices = async (apiKey: string) => {
  if (!apiKey) {
    return Promise.reject(new Error(strings.ERROR_APIKEY_NULL));
  }

  return axios
    .get(urlJoin(ELEVENLABS_API_BASE_ENDPOINT, 'v2', 'voices'), {
      headers: { [ELEVENLABS_XI_API_KEY_HEADER]: apiKey },
      params: {
        include_total_count: true,
        page_size: 100
      }
    })
    .then(response => {
      return response.data;
    });
};

export const fetchElevenLabsVoiceSettings = async (
  apiKey: string,
  voiceId: string
) => {
  if (!apiKey) {
    return Promise.reject(new Error(strings.ERROR_APIKEY_NULL));
  }

  return axios
    .get(
      urlJoin(
        ELEVENLABS_API_BASE_ENDPOINT,
        'v1',
        'voices',
        voiceId,
        'settings'
      ),
      {
        headers: { [ELEVENLABS_XI_API_KEY_HEADER]: apiKey }
      }
    )
    .then(response => {
      return response.data;
    });
};
